import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import Button from '../components/ui/Button';
import FileButton from '../components/ui/FileButton';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import { importHtmlPage } from '../import/htmlPageImport';
import { putImportDraft } from '../import/importHandoff';

/**
 * "Inhalt erstellen" from an interactive page - a zip holding one
 * self-contained HTML page (a "Klickprozess" export), or the .html itself.
 * It is converted to Markdown in the browser (import/htmlPageImport.js) and
 * opens in the ordinary editor, where it is reviewed and published; nothing
 * of the page is executed or uploaded.
 */
function CreateArticleHtml() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setBusy(true);
    setError('');
    setResult(null);

    try {
      setResult(await importHtmlPage(file));
    } catch (err) {
      console.error('Interactive page import failed:', err);
      const known = ['noPage', 'noContent'].includes(err?.message);
      setError(known ? t(`createArticleHtml.${err.message}`) : (err?.message || t('createArticleHtml.error')));
    } finally {
      setBusy(false);
    }
  }

  function openInEditor() {
    const importId = putImportDraft(result);
    navigate('/create-article/manual', { state: { importId } });
  }

  return (
    <PageLayout showSearch showAiSearch>
      <PageMain className="article-page">
        <PageHeader title={t('createArticleHtml.title')} condensed />

        <PageBody className="page-body--split">
          <div className="maincol markdown-content">
            <div className="pdf-upload-box">
              <i className="fa-solid fa-diagram-project" />
              <h2>{t('createArticleHtml.selectTitle')}</h2>
              <p>{t('createArticleHtml.description')}</p>

              <div className="rise-import-actions">
                <FileButton
                  variant="choice"
                  accept=".zip,application/zip,.html,.htm,text/html"
                  disabled={busy}
                  onChange={handleFile}
                >
                  <i className="fa-solid fa-file-zipper" />
                  {t('createArticleHtml.fileButton')}
                </FileButton>
              </div>

              {busy && <p>{t('createArticleHtml.processing')}</p>}

              {error && (
                <div className="login-message login-message-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{error}</span>
                </div>
              )}

              {result && (
                <div className="rise-import-result">
                  <h3>{result.title}</h3>
                  <p>
                    {t(result.kind === 'klickprozess' ? 'createArticleHtml.foundProcess' : 'createArticleHtml.foundPage', {
                      count: result.count,
                      words: result.text.split(/\s+/).filter(Boolean).length,
                    })}
                  </p>

                  {result.warnings.length > 0 && (
                    <ul className="rise-import-warnings">
                      {result.warnings.map((warning) => (
                        <li key={warning.key}>{t(`createArticleHtml.${warning.key}`, warning.values)}</li>
                      ))}
                    </ul>
                  )}

                  <Button variant="choice" onClick={openInEditor}>
                    <i className="fa-solid fa-pen-to-square" />
                    {t('createArticleHtml.openEditor')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default CreateArticleHtml;
